import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    CircularProgress,
    Alert,
    Grid,
    Card,
    CardContent,
    Pagination
} from '@mui/material';
import { styled } from '@mui/system';
import { FaUsers, FaSearch, FaFilter } from 'react-icons/fa';
import AppColors from '../../utils/AppColors';
import { getUsers } from '../../services/api';

const Container = styled(Box)({
    padding: '2rem',
});

const PageTitle = styled(Typography)({
    fontSize: '2rem',
    fontWeight: 'bold',
    color: AppColors.primaryDark,
    marginBottom: '2rem',
});

const StatsCard = styled(Card)({
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
});

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalUsers, setTotalUsers] = useState(0);

    useEffect(() => {
        fetchUsers();
    }, [page, searchTerm]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await getUsers({ 
                page, 
                limit: 20, 
                search: searchTerm 
            });
            setUsers(response.users || []);
            setTotalPages(response.totalPages || 1);
            setTotalUsers(response.total || 0);
        } catch (err) {
            setError('Failed to fetch users');
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setPage(1);
        fetchUsers();
    };

    if (loading && page === 1) {
        return (
            <Container>
                <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container>
            <PageTitle>User Management</PageTitle>
            
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Stats Card */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <Box sx={{ 
                                    p: 1, 
                                    borderRadius: '50%', 
                                    backgroundColor: `${AppColors.primaryDark}20`,
                                    mr: 2 
                                }}>
                                    <FaUsers color={AppColors.primaryDark} />
                                </Box>
                                <Box>
                                    <Typography variant="h4" fontWeight="bold" color={AppColors.primaryDark}>
                                        {totalUsers}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Total Users
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </StatsCard>
                </Grid>
            </Grid>

            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Box display="flex" gap={2} mb={3}>
                    <TextField
                        placeholder="Search users by name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        InputProps={{
                            startAdornment: <FaSearch style={{ marginRight: 8 }} />,
                        }}
                        sx={{ flexGrow: 1 }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleSearch}
                        startIcon={<FaFilter />}
                        sx={{
                            backgroundColor: AppColors.primaryDark,
                            '&:hover': { backgroundColor: AppColors.primaryYellow, color: AppColors.primaryDark }
                        }}
                    >
                        Search
                    </Button>
                </Box>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>Joined</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow key={user.id}>
                                    <TableCell>{user.name || 'N/A'}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>{user.phone || 'N/A'}</TableCell>
                                    <TableCell>
                                        {new Date(user.created_at).toLocaleDateString()}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {totalPages > 1 && (
                    <Box display="flex" justifyContent="center" mt={3}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(event, value) => setPage(value)}
                            color="primary"
                        />
                    </Box>
                )}
            </Paper>
        </Container>
    );
};

export default UserManagement;
